import React from "react";
import { Dialog, LinearProgress, keyframes } from "@mui/material";
import { styled } from "@mui/system";
import ContainedButton from "src/components/common/ContainedButton";
import { useHistory } from "react-router-dom";
import { trackEvent } from "src/Amplitude";
import { TransactionDisclosureModal } from "src/components/disclosures/TransactionDisclosureModal";
import useTransactionDisclosure, {
  type DisclosureMetadata,
} from "src/utils/hooks/useTransactionDisclosure";

// Keyframes for sliding in
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const DialogWrapper = styled(Dialog)(() => ({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  alignItems: "center",
  borderRadius: "10px",
  background: "#FFF",
}));

export const Wrapper = styled("div")(({ theme }) => ({
  display: "flex",
  width: "583px",
  height: "227px",
  flexDirection: "column",
  alignItems: "center",
  padding: "24px",
  gap: "16px",

  [theme.breakpoints.down("sm")]: {
    width: "100%",
    height: "100%",
    padding: "16px",
  },
}));

export const HeaderWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  alignSelf: "stretch",
  paddingBottom: "12px",
  borderBottom: "1px solid #E5E7E8",
}));

export const Header = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  alignSelf: "stretch",
  flex: 1,
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "18px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "22px",
}));

export const ContentWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  alignSelf: "stretch",
  borderRadius: "4px",
  background: "#FFF",
}));

interface TextProps {
  color?: string;
  weight?: number;
}
export const Text = styled("div")<TextProps>(({ color, weight }) => ({
  color: color ?? "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: weight ?? 400,
  lineHeight: "16px",
}));

export const UnderlineText = styled("div")(() => ({
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
  textDecorationLine: "underline",
}));

export const ButtonsWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
  alignSelf: "stretch",
}));

export const LoadingBarWrapper = styled("div")(() => ({
  width: "100%",
  height: "34px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  transition: "all 0.5s ease",
  animation: `${slideIn} 0.5s forwards`,
}));

interface TradeConfirmationModalProps {
  open: boolean;
  sellUnits: string;
  sellCode: string;
  buyUnits: string;
  buyCode: string;
  isTwoFAEnabled?: boolean;
  isLoading?: boolean;
  onClose: () => void;
  /** Called when user confirms; receives disclosure metadata (timestamps) when user accepted MN/CT disclosure */
  onConfirm: (disclosureMetadata?: DisclosureMetadata) => void;
}

/**
 * TradeConfirmationModal component
 *
 * This component is used to display a modal for trade order confirmation.
 * If the user does not have two-factor authentication (2FA) enabled, it warns the user
 * and provides an option to enable 2FA before proceeding with the trade order.
 */
const TradeConfirmationModal = (props: TradeConfirmationModalProps) => {
  const {
    open,
    sellUnits,
    sellCode,
    buyUnits,
    buyCode,
    isTwoFAEnabled,
    isLoading,
    onClose,
    onConfirm,
  } = props;

  const history = useHistory();

  // ================= Transaction-specific disclosure =================
  const [showDisclosureModal, setShowDisclosureModal] = React.useState<boolean>(false);
  const [pendingConfirm, setPendingConfirm] = React.useState<boolean>(false);

  // Don't auto-open disclosure - we'll control it manually
  const {
    transactionDisclosure,
    requiresDisclosure,
    isLoading: isDisclosureLoading,
    disclosurePayload,
    modalProps,
    closeModal: handleDisclosureClose,
  } = useTransactionDisclosure(
    (disclosureId: string, metadata?: DisclosureMetadata) => {
      if (pendingConfirm) {
        onConfirm(metadata);
      }
      setPendingConfirm(false);
      setShowDisclosureModal(false);
    },
    "trade",
  );

  // Override the modal open state to control it manually
  const disclosureModalProps = React.useMemo(
    () => ({
      ...modalProps,
      open: showDisclosureModal && !!transactionDisclosure,
      onClose: () => {
        // If disclosure is closed without accepting, just close the disclosure modal
        setShowDisclosureModal(false);
        setPendingConfirm(false);
        handleDisclosureClose();
      },
    }),
    [modalProps, showDisclosureModal, transactionDisclosure, handleDisclosureClose],
  );

  // Handle confirm button click
  const handleConfirmClick = () => {
    // Don't proceed while the disclosure is still resolving — a null transactionDisclosure
    // (and thus requiresDisclosure === false) means "unknown", not "not required".
    if (isDisclosureLoading) {
      return;
    }

    // Check if disclosure is required (MN/CT and not already accepted today)
    if (requiresDisclosure && !showDisclosureModal) {
      // Store the confirmation intent and show disclosure modal
      setPendingConfirm(true);
      setShowDisclosureModal(true);
      return;
    }

    // If no disclosure required, pass any existing payload (e.g. already accepted today) so backend gets disclosure fields
    onConfirm(disclosurePayload ?? undefined);
  };

  const formatNumber = (units: string): string => {
    return new Intl.NumberFormat().format(Number(units));
  };

  const config = {
    "2faDisabledConfig": {
      header: "Two Factor Authentication",
      subtitle: "It looks like you don't have two factor authentication enabled",
      underline: "Enable it now to continue your order",
      description: "Click the button below to enable two factor authentication.",
      button1: "Go Back",
      button2: "Enable Two Factor",
      button1Func: onClose,
      button2Func: () => history.push("settings/security"),
    },
    "2faEnbledConfig": {
      header: "Order Confirmation",
      subtitle: "Confirm your order",
      underline: "You are actioning the below trade order:",
      description: `You are selling ${formatNumber(
        sellUnits,
      )} ${sellCode} to buy Approx. ${buyUnits} ${buyCode}.`,
      button1: "Edit Order",
      button2: "Confirm",
      button1Func: onClose,
      button2Func: handleConfirmClick,
    },
  };

  const twoFAData = isTwoFAEnabled ? config["2faEnbledConfig"] : config["2faDisabledConfig"];

  React.useEffect(() => {
    if (open) {
      if (!isTwoFAEnabled) {
        trackEvent("Visit Modal", { modal: "Place Order 2FA Warning" }, "Trade");
      } else {
        trackEvent("Visit Modal", { modal: "Place Order Confirmation" }, "Trade");
      }
    }
  }, [open]);

  return (
    <DialogWrapper open={open} onClose={onClose} sx={{ background: "rgba(0, 32, 53, 0.7)" }}>
      <Wrapper>
        <HeaderWrapper>
          <Header>{twoFAData.header}</Header>
        </HeaderWrapper>
        <ContentWrapper>
          <Text color="#FC4C4C" weight={600}>
            {twoFAData.subtitle}
          </Text>
          <UnderlineText>{twoFAData.underline}</UnderlineText>
          <Text>{twoFAData.description}</Text>
        </ContentWrapper>
        {isLoading ? (
          <LoadingBarWrapper>
            <LinearProgress />
          </LoadingBarWrapper>
        ) : (
          <ButtonsWrapper>
            <ContainedButton
              color="neutral"
              borderRadius="4px"
              height="32px"
              onClick={twoFAData.button1Func}
              sx={{ background: "#F8F9F9" }}>
              <Text>{twoFAData.button1}</Text>
            </ContainedButton>
            <ContainedButton
              id={isTwoFAEnabled ? "confirm-order-button" : "2fa-enable-button"}
              color="salmon"
              borderRadius="4px"
              height="32px"
              onClick={twoFAData.button2Func}
              sx={{ boxShadow: "0px 4px 24px 0px rgba(255, 57, 57, 0.24)" }}>
              <Text color="#FFF" weight={600}>
                {twoFAData.button2}
              </Text>
            </ContainedButton>
          </ButtonsWrapper>
        )}
      </Wrapper>

      {/* TRANSACTION-SPECIFIC DISCLOSURE MODAL */}
      {transactionDisclosure && <TransactionDisclosureModal {...disclosureModalProps} />}
    </DialogWrapper>
  );
};

export default TradeConfirmationModal;
